/**
 * Textos da ajuda contextual (drawer) das telas PCO / Recursos.
 * Manter linguagem alinhada à documentação do módulo (orçamento formal, faturas, rateio).
 */

export type PcoHelpAlertType = 'info' | 'warning' | 'error' | 'success';

export interface PcoHelpAlert {
    type: PcoHelpAlertType;
    text: string;
}

export interface PcoHelpSection {
    title: string;
    paragraph?: string;
    bullets?: string[];
}

export interface PcoHelpContent {
    /** Título no topo do painel lateral */
    panelTitle: string;
    intro: string;
    sections?: PcoHelpSection[];
    alerts?: PcoHelpAlert[];
}

export const PCO_HELP_PAGE_KEYS = [
    'dashboard',
    'plan-vs-actual',
    'budgets',
    'budget-detail',
    'contracts',
    'invoices',
    'allocations',
    'allocation-calculator',
    'closings',
    'reports'
] as const;

export type PcoHelpPageKey = (typeof PCO_HELP_PAGE_KEYS)[number];

export const resourcesPcoHelpByPage: Record<PcoHelpPageKey, PcoHelpContent> = {
    dashboard: {
        panelTitle: 'Dashboard executivo (PCO)',
        intro:
            'Visão consolidada do ano: previsto a partir do orçamento formal, realizado nas faturas e projeção conforme a regra escolhida.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Selecione o ano, o orçamento (opcional) e a regra de projeção para meses futuros.',
                    'Use os atalhos para o comparativo mensal ou para a calculadora de rateio.',
                    'Os KPIs e gráficos refletem o orçamento ativo e as faturas registradas.'
                ]
            },
            {
                title: 'Ideal para',
                bullets: [
                    'Acompanhar consumo vs orçamento e saúde projetada',
                    'Ver distribuição por categoria, BU e CAPEX/OPEX',
                    'Identificar maiores despesas cobradas no ano'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'O previsto executivo vem do orçamento (pco_budget), não da soma de valores planejados apenas nas faturas.'
            }
        ]
    },
    'plan-vs-actual': {
        panelTitle: 'Previsto × consolidado',
        intro:
            'Compara mês a mês o programado do orçamento com o consolidado das faturas PCO (valor cobrado), incluindo variações acumuladas.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Escolha o ano e, se quiser, um orçamento específico; sem seleção, usa o padrão do ano.',
                    'Analise a tabela por mês: período (passado, atual, futuro), programado, consolidado e variações.',
                    'O gráfico combina colunas mensais com a linha de variação acumulada.'
                ]
            }
        ],
        alerts: [
            {
                type: 'warning',
                text: 'Meses futuros podem mostrar apenas expectativa de orçamento, sem consolidado real.'
            }
        ]
    },
    budgets: {
        panelTitle: 'Orçamentos',
        intro:
            'Lista os orçamentos formais PCO. Cada orçamento possui cabeçalho por mês e linhas analíticas (P&D Infra) que alimentam o previsto no dashboard.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Crie um novo orçamento informando ano, nome, total planejado e tipo de distribuição mensal.',
                    'Abra um orçamento na lista para editar meses e linhas.',
                    'Volte ao dashboard para ver os KPIs com base no orçamento escolhido.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Sem orçamento cadastrado, parte dos indicadores e comparativos pode ficar vazia ou usar apenas dados de faturas.'
            }
        ]
    },
    'budget-detail': {
        panelTitle: 'Detalhe do orçamento',
        intro:
            'Edição do orçamento selecionado: abas para meses do cabeçalho e para linhas P&D. Esses valores definem o previsto usado nos relatórios executivos.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Ajuste valores por mês quando o cabeçalho refletir a curva anual.',
                    'Inclua ou altere linhas analíticas conforme categorias e contas do plano.',
                    'Salve alterações antes de sair; recarregue o dashboard para validar o impacto.'
                ]
            }
        ],
        alerts: [
            {
                type: 'warning',
                text: 'Alterar um orçamento já usado em fechamentos pode exigir revisão de competências fechadas — siga o processo interno de governança.'
            }
        ]
    },
    contracts: {
        panelTitle: 'Contratos PCO',
        intro:
            'Contratos vinculados a itens do ITAM (licença, linha ou dispositivo). Permitem gerar faturas de forma idempotente quando a regra do contrato estiver configurada.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Revise vínculos ITAM e vigência de cada contrato.',
                    'Use a ação de gerar faturas quando aplicável; o sistema evita duplicar gerações indevidas.',
                    'Acompanhe mensagens de retorno após a geração.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Contratos sem vínculo ITAM adequado podem não refletir corretamente o rateio por colaborador/departamento.'
            }
        ]
    },
    invoices: {
        panelTitle: 'Faturas',
        intro:
            'Competências de cobrança: valores planejados, cobrados e pagos. Permite registrar pagamento em itens em aberto e acompanhar status e vencimentos.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Filtre pelo ano (sincronizado com a preferência do store PCO quando existir).',
                    'Abra o fluxo de pagamento para informar valor pago, data e forma.',
                    'Itens em atraso podem ser destacados visualmente conforme vencimento.'
                ]
            }
        ],
        alerts: [
            {
                type: 'warning',
                text: 'Em competências fechadas, a edição de certos campos pode estar bloqueada para preservar o fechamento — administradores seguem regras do backend.'
            }
        ]
    },
    allocations: {
        panelTitle: 'Rateio por departamento',
        intro:
            'Distribuição dos custos das faturas por departamento, usando atribuições ITAM (usuário → departamento) ou fallbacks (departamento na fatura, contrato, etc.).',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Selecione o ano e analise as linhas por departamento e origem da atribuição.',
                    'Compare totais agregados com o que foi faturado no período.',
                    'Use a coluna de origem para entender de onde veio cada rateio.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Quando não há vínculo ITAM, o sistema aplica regras de fallback descritas na própria listagem (rótulos por fonte).'
            }
        ]
    },
    'allocation-calculator': {
        panelTitle: 'Calculadora de rateio',
        intro:
            'Verifica consistência entre o total faturado no ano e o valor distribuído por departamento, com indicadores visuais (ok / atenção / crítico) alinhados à planilha de controle.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Escolha o ano e interprete o status geral e os desvios exibidos.',
                    'Use os detalhes para localizar departamentos ou competências com cobertura incompleta.',
                    'Cruze com a tela de rateio por departamento e com as faturas para corrigir dados na origem.'
                ]
            }
        ],
        alerts: [
            {
                type: 'warning',
                text: 'A calculadora não altera dados; apenas diagnostica. Ajustes são feitos em faturas, contratos ou ITAM.'
            }
        ]
    },
    closings: {
        panelTitle: 'Fechamentos',
        intro:
            'Registro de fechamento mensal ou anual por orçamento. Após fechado, alterações sensíveis nas faturas da competência podem ser bloqueadas; reabertura costuma ser restrita a administradores.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Escolha orçamento, ano, tipo (mensal ou anual) e competência quando for mensal.',
                    'Registre observações que facilitem auditoria futura.',
                    'Consulte a lista de fechamentos e use reabertura apenas quando o processo interno permitir.'
                ]
            }
        ],
        alerts: [
            {
                type: 'error',
                text: 'Fechamento é operação sensível: confira valores e competência antes de confirmar.'
            }
        ]
    },
    reports: {
        panelTitle: 'Relatórios',
        intro:
            'Exportações em PDF ou arquivos derivados da base PCO (faturas anuais, base de despesas com rateio, etc.), conforme os botões disponíveis na tela.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Selecione o ano alinhado às exportações oferecidas.',
                    'Inicie cada download e aguarde; erros de rede ou permissão aparecem na própria página.',
                    'Guarde os arquivos conforme política de documentação financeira.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'O conteúdo exato de cada relatório segue as regras do backend (filtros, fechamentos e dados consolidados).'
            }
        ]
    }
};

export function getPcoHelpContent(key: PcoHelpPageKey): PcoHelpContent {
    return resourcesPcoHelpByPage[key];
}
